import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CompanyAddonDetail, getCompanyAddon } from '@services/companyAddons';
import { facilitySessionId } from '@utils/billingFacilityUtils';
import { getSelectedFacilityFromSession } from '@utils/facilityUtils';

export default function AddonDetail({ addonId }: { addonId: string }) {
  const navigate = useNavigate();
  const [addon, setAddon] = useState<CompanyAddonDetail>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const facility = getSelectedFacilityFromSession<Record<string, unknown>>();
    getCompanyAddon(addonId, facilitySessionId(facility))
      .then(setAddon)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Unable to load add-on'))
      .finally(() => setLoading(false));
  }, [addonId]);

  if (loading) return <CircularProgress aria-label="Loading add-on" size={28} />;
  if (error || !addon) return <Typography color="error">{error || 'Add-on not found'}</Typography>;

  const active = addon.subscription_state === 'active';
  const pending = Number(addon.pending_request_count || 0);
  const usage = addon.usage || addon.subscription?.usage;
  const needed = Number(addon.aggregate?.total_needed || 0);
  const remaining = Number(addon.aggregate?.remaining ?? usage?.remaining ?? 0);

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Button onClick={() => navigate('/dashboard/billing?tab=addons')} sx={{ justifySelf: 'start' }}>
        ← Back to Add-ons
      </Button>
      <Typography variant="h5" sx={{ color: '#065b70', fontWeight: 600 }}>{addon.name}</Typography>
      <Typography>{addon.description || 'Add capacity when your facility needs it.'}</Typography>
      {active && pending > 0 ? (
        <>
          <Typography>
            {needed || pending} people are waiting on this add-on. {remaining} units remain.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/dashboard/billing?tab=addons&addon=${addonId}&step=1`)}
          >
            Upgrade &amp; approve all
          </Button>
        </>
      ) : active ? (
        <Typography>
          You&apos;re covered — {remaining} units remaining
          {usage ? ` of ${usage.total}` : ''}.
        </Typography>
      ) : addon.subscription_state === 'coming_soon' ? (
        <Typography>This add-on is coming soon.</Typography>
      ) : (
        <Box data-testid="addon-not-activated-state" sx={{ display: 'grid', gap: 1 }}>
          <Typography>Available for your account. Starts at ${addon.starting_price || '0'}/yr.</Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/dashboard/billing?tab=addons&addon=${addonId}&step=1`)}
          >
            Learn how it works
          </Button>
          <Link href="mailto:sales@affableone.ai">Contact sales</Link>
        </Box>
      )}
      {active ? (
        <Box
          component="details"
          sx={{ mt: 1, borderTop: '1px solid #d8e8ec', pt: 1, fontFamily: '"Rethink Sans", sans-serif' }}
        >
          <Box
            component="summary"
            sx={{
              cursor: 'pointer',
              color: '#065b70',
              fontWeight: 600,
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: '14px',
            }}
          >
            Details &amp; manage
          </Box>
          <Box sx={{ display: 'grid', gap: 1, pt: 2 }}>
            <Typography variant="body2">
              Current tier: <strong>{addon.current_tier_name || 'Active'}</strong>
            </Typography>
            <Typography variant="body2">
              Usage: {usage?.used ?? 0}/{usage?.total ?? 0} used · {remaining} remaining
            </Typography>
            <Typography variant="body2">
              Individual requests: {addon.requests?.length || 0}
            </Typography>
            {addon.requests?.length ? (
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 0.75fr', gap: 1, fontWeight: 600 }}>
                  <Typography variant="caption">Requester</Typography>
                  <Typography variant="caption">Facility</Typography>
                  <Typography variant="caption">Request</Typography>
                  <Typography variant="caption">When</Typography>
                </Box>
                {[...addon.requests]
                  .sort((left, right) => Date.parse(right.created_at || '') - Date.parse(left.created_at || ''))
                  .map((request) => (
                    <Box key={request.id} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr 0.75fr', gap: 1 }}>
                      <Typography variant="caption">{request.requester_name || '—'}</Typography>
                      <Typography variant="caption">{(request as { facility_name?: string }).facility_name || '—'}</Typography>
                      <Typography variant="caption">Capacity request</Typography>
                      <Typography variant="caption">
                        {request.created_at ? new Date(request.created_at).toLocaleDateString() : '—'}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/dashboard/billing?tab=addons&addon=${addonId}&step=1`)}
              >
                Change tier
              </Button>
              <Button variant="outlined" color="error" disabled>
                Cancel add-on
              </Button>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
